import StyledCard from "components/UI/StyledCard"
import { GridColDef, GridRowId, GridValidRowModel, GridValueFormatter } from '@mui/x-data-grid-premium'
import CheckIcon from '@mui/icons-material/Check'
import { getIn } from "components/Formik/forms"
import { useMemo } from "react"
import { CopyValueCell } from "./common/CopyValueCell"
import { getRequestParameterRows } from "./data/helper"
import TreeDataGrid from "./common/TreeDataGrid"
import { useOrbit } from "providers/OrbitProvider"

type RowWithId = GridValidRowModel & { id: GridRowId }

export const hideSubgroupNameFormatter: GridValueFormatter<RowWithId, unknown, string> = (value, row, _colDef, apiRef) => {
  const id = row.id
  const node = apiRef.current.getRowNode(id)
  if (node?.depth != null && node.depth > 0) return ''
  return (value ?? '') as string
}

const requestParameterColumns: GridColDef[] = [
  {
    field: 'name',
    groupable: true,
    headerName: 'Field',
    flex: 1.75,
    cellClassName: 'code-text bold',
    renderCell: (params) => {
      if (params.rowNode?.depth > 0) return ''
      return params.value
    },
    valueFormatter: hideSubgroupNameFormatter,
  },
  {
    field: 'required',
    groupable: false,
    headerName: 'Required',
    flex: 0.75,
    headerAlign: 'center',
    align: 'center',
    renderCell: (params) => (params.value === true ? <CheckIcon sx={{ verticalAlign: 'text-bottom' }} /> : ''),
    // export text instead of an icon
    valueFormatter: (value) => (value === true ? 'Yes' : ''),
  },
  {
    field: 'description',
    groupable: false,
    headerName: 'Description',
    cellClassName: 'bold',
    flex: 1.5,
    colSpan: (_value, row) => (row.path.length === 1 ? 3 : undefined),
  },
  {
    field: 'value',
    groupable: false,
    headerName: 'Value',
    flex: 1.5,
    cellClassName: 'code-text bold',
    renderCell: CopyValueCell,
    valueFormatter: (value) => (value ?? ''),
  },
  {
    field: 'text',
    groupable: false,
    headerName: 'Value Detail',
    flex: 3.5,
  },
  {
    field: '__tree_data_group__', // The grouping column (used to order it last)
    width: 100
  }
]

export function RequestParameters({clientCampaigns, fields, degreePrograms, leadsEndpoint}) {
  const { getRemoteId } = useOrbit()

  const rows = useMemo(() => getRequestParameterRows({
    clientCampaigns: clientCampaigns,
    fields: fields,
    degreePrograms: degreePrograms,
    parameterNames: getIn(leadsEndpoint, 'attributes.parameterNames') || {},
    getRemoteId,
  }), [clientCampaigns, fields, degreePrograms, leadsEndpoint, getRemoteId])

  return (
    <StyledCard
    title='Request Parameters'
    isSubsection={true}
    >
      <TreeDataGrid
        rows={rows}
        columns={requestParameterColumns}
        exportRegistration={{ id: 'request-params', sheetName: 'Request Parameters', order: 1 }}
      />
    </StyledCard>
  )
}
